import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, {
  type Map as MapType,
  type LngLatBoundsLike,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { irisDarkStyle } from './style';
import { useActiveFocus, useData, useUI, inFocus } from '@/data/stores';
import {
  buildAssetGeoJSON,
  buildDependencyGeoJSON,
  buildVesselGeoJSON,
  buildAlertHaloGeoJSON,
  buildScenarioRingGeoJSON,
  buildGeofenceGeoJSON,
  buildInfrastructureGeoJSON,
  buildWeatherGeoJSON,
} from './geojson';
import { ContextPanel } from './ContextPanel';
import { LayerPanel } from './LayerPanel';
import { Legend } from './Legend';
import './map.css';


export function MapView() {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapType | null>(null);
  const [hover, setHover] = useState<{ x: number; y: number; text: string } | null>(
    null,
  );
  const [mapReady, setMapReady] = useState(false);
  const allAssets = useData((s) => s.assets);
  const allEdges = useData((s) => s.edges);
  const allAlerts = useData((s) => s.alerts);
  const allVessels = useData((s) => s.vessels);
  const ui = useUI();
  const focus = useActiveFocus();

  // Filter through the active focus mode so the map always reflects what the
  // user has chosen to see.
  const assets = useMemo(() => allAssets.filter((a) => inFocus(focus, a)), [allAssets, focus]);
  const assetIds = useMemo(() => new Set(assets.map((a) => a.asset_id)), [assets]);
  const edges = useMemo(
    () => allEdges.filter((e) => assetIds.has(e.from_asset_id) || assetIds.has(e.to_asset_id)),
    [allEdges, assetIds],
  );
  const alerts = useMemo(
    () => allAlerts.filter((a) => a.asset_ids.some((id) => assetIds.has(id))),
    [allAlerts, assetIds],
  );
  const vessels = focus.id === 'global' ? allVessels : allVessels.filter(() => true);

  // ───────────── Map init ─────────────
  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: ref.current,
      style: irisDarkStyle,
      center: focus.center as [number, number],
      zoom: focus.zoom,
      maxZoom: 14,
      minZoom: 2,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-right');
    mapRef.current = map;

    map.on('load', () => {
      // ──── Sources ────
      map.addSource('iris-assets', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addSource('iris-deps', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addSource('iris-geofences', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addSource('iris-weather', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addSource('iris-infra-network', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addSource('iris-vessels', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addSource('iris-halos', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addSource('iris-rings', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      // ──── Weather envelopes ────
      map.addLayer({
        id: 'iris-weather-fill',
        type: 'fill',
        source: 'iris-weather',
        paint: {
          'fill-color': ['get', 'color'],
          'fill-opacity': [
            'match',
            ['get', 'severity'],
            'critical',
            0.16,
            'warning',
            0.11,
            0.08,
          ],
        },
      });
      map.addLayer({
        id: 'iris-weather-line',
        type: 'line',
        source: 'iris-weather',
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.8, 8, 1.5],
          'line-opacity': 0.78,
        },
      });
      map.addLayer({
        id: 'iris-weather-label',
        type: 'symbol',
        source: 'iris-weather',
        layout: {
          'text-field': [
            'format',
            ['get', 'name'],
            { 'font-scale': 1 },
            '\n',
            {},
            ['get', 'phenomenon'],
            { 'font-scale': 0.78 },
          ],
          'text-font': ['Open Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 3, 10, 7, 12],
          'text-anchor': 'center',
          'text-allow-overlap': false,
          'symbol-placement': 'point',
        },
        paint: {
          'text-color': '#dcdcdc',
          'text-halo-color': '#0e1316',
          'text-halo-width': 1.6,
          'text-opacity': 0.92,
        },
      });

      // ──── Asset geofences ────
      map.addLayer({
        id: 'iris-geofence-fill',
        type: 'fill',
        source: 'iris-geofences',
        paint: {
          'fill-color': ['get', 'color'],
          'fill-opacity': 0.055,
        },
      });
      map.addLayer({
        id: 'iris-geofence-line',
        type: 'line',
        source: 'iris-geofences',
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.9, 9, 1.8],
          'line-opacity': 0.82,
        },
      });
      map.addLayer({
        id: 'iris-geofence-label',
        type: 'symbol',
        source: 'iris-geofences',
        minzoom: 5.2,
        layout: {
          'text-field': [
            'format',
            ['get', 'zoneType'],
            { 'font-scale': 0.82 },
            '\n',
            {},
            ['get', 'shortName'],
            { 'font-scale': 0.72 },
          ],
          'text-font': ['Open Sans Regular'],
          'text-size': 10,
          'text-anchor': 'center',
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#dcdcdc',
          'text-halo-color': '#0e1316',
          'text-halo-width': 1.4,
          'text-opacity': 0.9,
        },
      });

      // ──── Infrastructure network corridors ────
      map.addLayer({
        id: 'iris-infra-network-line',
        type: 'line',
        source: 'iris-infra-network',
        paint: {
          'line-color': [
            'match',
            ['get', 'relation'],
            'feeds',
            '#5b9bd5',
            'supplies',
            '#7ec8a0',
            'controls',
            '#d4a85c',
            'communicates_with',
            '#8fb0cc',
            'shares_corridor',
            '#9ca3a9',
            '#6b7279',
          ],
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            3,
            ['*', ['get', 'criticality'], 0.35],
            8,
            ['*', ['get', 'criticality'], 0.7],
          ],
          'line-opacity': [
            'case',
            ['==', ['get', 'hasBackup'], 'true'],
            0.28,
            0.48,
          ],
        },
      });

      // ──── Dependency lines ────
      map.addLayer({
        id: 'iris-deps-line',
        type: 'line',
        source: 'iris-deps',
        paint: {
          'line-color': [
            'match',
            ['get', 'relation'],
            'feeds',
            '#5b9bd5',
            'supplies',
            '#7ec8a0',
            'controls',
            '#d4a85c',
            'communicates_with',
            '#8fb0cc',
            'shares_corridor',
            '#6b7279',
            '#3c4347',
          ],
          'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.6, 8, 1.4],
          'line-opacity': 0.55,
          'line-dasharray': [2, 2],
        },
      });

      // ──── Critical-alert pulsing halos ────
      map.addLayer({
        id: 'iris-halo',
        type: 'circle',
        source: 'iris-halos',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 3, 14, 8, 26],
          'circle-color': '#e53935',
          'circle-opacity': 0.18,
          'circle-blur': 0.5,
        },
      });

      // ──── Asset markers (circles) ────
      map.addLayer({
        id: 'iris-assets-circles',
        type: 'circle',
        source: 'iris-assets',
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            2, ['*', ['get', 'sizeBase'], 0.6],
            6, ['get', 'sizeBase'],
            10, ['*', ['get', 'sizeBase'], 1.4],
          ],
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.9,
          'circle-stroke-color': '#0e1316',
          'circle-stroke-width': 1.5,
        },
      });

      // ──── Asset labels (sector icons / text) ────
      map.addLayer({
        id: 'iris-assets-labels',
        type: 'symbol',
        source: 'iris-assets',
        minzoom: 5.5,
        layout: {
          'text-field': ['get', 'shortName'],
          'text-font': ['Open Sans Regular'],
          'text-size': 10,
          'text-offset': [0, 1.2],
          'text-anchor': 'top',
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#dcdcdc',
          'text-halo-color': '#0e1316',
          'text-halo-width': 1.4,
        },
      });

      // ──── Vessels ────
      map.addLayer({
        id: 'iris-vessels-layer',
        type: 'circle',
        source: 'iris-vessels',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 3, 2, 9, 5],
          'circle-color': [
            'match',
            ['get', 'aisStatus'],
            'normal',
            '#9CA3A9',
            'gap',
            '#F5A623',
            'spoofed',
            '#E53935',
            'dark',
            '#E53935',
            '#9CA3A9',
          ],
          'circle-opacity': 0.85,
          'circle-stroke-color': '#0e1316',
          'circle-stroke-width': 0.6,
        },
      });

      // ──── Scenario "affected" rings ────
      map.addLayer({
        id: 'iris-rings-layer',
        type: 'circle',
        source: 'iris-rings',
        paint: {
          'circle-radius': 18,
          'circle-color': 'transparent',
          'circle-stroke-color': '#e53935',
          'circle-stroke-width': 2,
          'circle-opacity': 0.9,
        },
      });

      // ──── Hover/click handlers ────
      map.on('mouseenter', 'iris-assets-circles', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'iris-assets-circles', () => {
        map.getCanvas().style.cursor = '';
        setHover(null);
      });
      map.on('mousemove', 'iris-assets-circles', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const props = f.properties as Record<string, string>;
        setHover({
          x: e.point.x,
          y: e.point.y,
          text: `${props['name']} · ${props['country']} · ${props['type']?.replace(/_/g, ' ')} · ${props['operationalStatus']}`,
        });
      });
      map.on('click', 'iris-assets-circles', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const id = (f.properties as Record<string, string>)['asset_id']!;
        useUI.getState().setSelectedAsset(id);
        const lng = (f.geometry as GeoJSON.Point).coordinates[0]!;
        const lat = (f.geometry as GeoJSON.Point).coordinates[1]!;
        map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 6.5), duration: 500 });
      });

      map.on('mouseenter', 'iris-vessels-layer', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'iris-vessels-layer', () => {
        map.getCanvas().style.cursor = '';
        setHover(null);
      });
      map.on('mousemove', 'iris-vessels-layer', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const props = f.properties as Record<string, string>;
        setHover({
          x: e.point.x,
          y: e.point.y,
          text: `${props['name']} · MMSI ${props['mmsi']} · ${props['speedKnots']}kn · AIS ${props['aisStatus']}`,
        });
      });

      map.on('mouseenter', 'iris-weather-fill', () => {
        map.getCanvas().style.cursor = 'help';
      });
      map.on('mouseleave', 'iris-weather-fill', () => {
        map.getCanvas().style.cursor = '';
        setHover(null);
      });
      map.on('mousemove', 'iris-weather-fill', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const props = f.properties as Record<string, string>;
        setHover({
          x: e.point.x,
          y: e.point.y,
          text: `${props['name']} · ${props['validWindow']} · ${props['affectedAssets']} exposed assets · ${props['confidence']}% confidence`,
        });
      });

      map.on('mouseenter', 'iris-geofence-fill', () => {
        map.getCanvas().style.cursor = 'help';
      });
      map.on('mouseleave', 'iris-geofence-fill', () => {
        map.getCanvas().style.cursor = '';
        setHover(null);
      });
      map.on('mousemove', 'iris-geofence-fill', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const props = f.properties as Record<string, string>;
        setHover({
          x: e.point.x,
          y: e.point.y,
          text: `${props['zoneType']} · ${props['name']} · ${props['radiusKm']} km · ${props['source']}`,
        });
      });
      setMapReady(true);
    });

    return () => {
      setMapReady(false);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ───────────── Update sources when data changes ─────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.isStyleLoaded()) return;
    const src = map.getSource('iris-assets') as maplibregl.GeoJSONSource | undefined;
    src?.setData(buildAssetGeoJSON(assets));
  }, [assets, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.isStyleLoaded()) return;
    const src = map.getSource('iris-deps') as maplibregl.GeoJSONSource | undefined;
    src?.setData(
      ui.layerVisibility.dependencies
        ? buildDependencyGeoJSON(edges, assets)
        : { type: 'FeatureCollection', features: [] },
    );
  }, [edges, assets, ui.layerVisibility.dependencies, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.isStyleLoaded()) return;
    const src = map.getSource('iris-geofences') as maplibregl.GeoJSONSource | undefined;
    src?.setData(
      ui.layerVisibility.geofences
        ? buildGeofenceGeoJSON(assets)
        : { type: 'FeatureCollection', features: [] },
    );
  }, [assets, ui.layerVisibility.geofences, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.isStyleLoaded()) return;
    const src = map.getSource('iris-weather') as maplibregl.GeoJSONSource | undefined;
    src?.setData(
      ui.layerVisibility.weather
        ? buildWeatherGeoJSON(assets)
        : { type: 'FeatureCollection', features: [] },
    );
  }, [assets, ui.layerVisibility.weather, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.isStyleLoaded()) return;
    const src = map.getSource('iris-infra-network') as maplibregl.GeoJSONSource | undefined;
    src?.setData(
      ui.layerVisibility.infraNetwork
        ? buildInfrastructureGeoJSON(edges, assets)
        : { type: 'FeatureCollection', features: [] },
    );
  }, [edges, assets, ui.layerVisibility.infraNetwork, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.isStyleLoaded()) return;
    const src = map.getSource('iris-vessels') as maplibregl.GeoJSONSource | undefined;
    src?.setData(
      ui.layerVisibility.vessels
        ? buildVesselGeoJSON(vessels)
        : { type: 'FeatureCollection', features: [] },
    );
  }, [vessels, ui.layerVisibility.vessels, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.isStyleLoaded()) return;
    const haloSrc = map.getSource('iris-halos') as maplibregl.GeoJSONSource | undefined;
    haloSrc?.setData(
      ui.layerVisibility.threats
        ? buildAlertHaloGeoJSON(alerts, assets)
        : { type: 'FeatureCollection', features: [] },
    );
  }, [alerts, assets, ui.layerVisibility.threats, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.isStyleLoaded()) return;
    const ringSrc = map.getSource('iris-rings') as maplibregl.GeoJSONSource | undefined;
    ringSrc?.setData(
      ui.scenarioOverlayActive
        ? buildScenarioRingGeoJSON(ui.scenarioAffectedIds, assets)
        : { type: 'FeatureCollection', features: [] },
    );
  }, [ui.scenarioOverlayActive, ui.scenarioAffectedIds, assets, mapReady]);

  // ───────────── Visibility toggles ─────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.isStyleLoaded()) return;
    const vis = (id: string, on: boolean) =>
      map.getLayer(id) && map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
    vis('iris-assets-circles', ui.layerVisibility.assets);
    vis('iris-assets-labels', ui.layerVisibility.assets);
    vis('iris-deps-line', ui.layerVisibility.dependencies);
    vis('iris-geofence-fill', ui.layerVisibility.geofences);
    vis('iris-geofence-line', ui.layerVisibility.geofences);
    vis('iris-geofence-label', ui.layerVisibility.geofences);
    vis('iris-weather-fill', ui.layerVisibility.weather);
    vis('iris-weather-line', ui.layerVisibility.weather);
    vis('iris-weather-label', ui.layerVisibility.weather);
    vis('iris-infra-network-line', ui.layerVisibility.infraNetwork);
    vis('iris-vessels-layer', ui.layerVisibility.vessels);
    vis('iris-halo', ui.layerVisibility.threats);
  }, [ui.layerVisibility, mapReady]);

  // ───────────── Auto-fit on focus mode change ─────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    map.fitBounds(focus.bounds as LngLatBoundsLike, { padding: 64, duration: 600 });
  }, [focus.id, focus.bounds, mapReady]);

  // ───────────── Fit bounds shortcut (F) ─────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key.toLowerCase() === 'f') {
        mapRef.current?.fitBounds(focus.bounds as LngLatBoundsLike, { padding: 64, duration: 600 });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focus.bounds]);

  // Fly to selected asset
  const selectedId = ui.selectedAssetId;
  useEffect(() => {
    if (!selectedId) return;
    const a = assets.find((x) => x.asset_id === selectedId);
    if (!a) return;
    mapRef.current?.flyTo({
      center: [a.coordinates.lon, a.coordinates.lat],
      zoom: Math.max(mapRef.current.getZoom(), 6.5),
      duration: 500,
    });
  }, [selectedId, assets]);

  const closeContext = useCallback(() => useUI.getState().setSelectedAsset(undefined), []);

  // Stats for the overlay
  const overlayStats = useMemo(() => {
    const operational = assets.filter((a) => a.operational_status === 'operational').length;
    const degraded = assets.filter((a) => a.operational_status === 'degraded').length;
    const offline = assets.filter((a) => a.operational_status === 'offline').length;
    return { total: assets.length, operational, degraded, offline };
  }, [assets]);

  return (
    <div className="iris-map-root">
      <div ref={ref} className="iris-map-container" />
      <Legend />
      <LayerPanel />
      <div className="iris-map-stats">
        <div>
          <span className="mono">{overlayStats.total}</span> assets
        </div>
        <div>
          <span className="mono">{overlayStats.operational}</span> operational
        </div>
        <div>
          <span className="mono">{overlayStats.degraded}</span> degraded
        </div>
        <div>
          <span className="mono">{overlayStats.offline}</span> offline
        </div>
        <div className="caption">[F] fit · [click] select · [⌘K] search</div>
      </div>
      {hover && (
        <div className="iris-map-hover" style={{ left: hover.x + 12, top: hover.y + 12 }}>
          {hover.text}
        </div>
      )}
      {ui.contextOpen && <ContextPanel onClose={closeContext} />}
    </div>
  );
}
